import { CheckIcon, ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/lumen/dialog';
import { cn } from '@/lib/utils';
import { collapsePatchLogsByVersion } from './collapse';
import { PatchLogEntryCard } from './PatchLogEntryCard';
import { PatchLogLocaleSwitcher } from './PatchLogLocaleSwitcher';
import { formatPatchLogDate, formatPatchLogVersionLabel, resolvePatchLogCopy } from './i18n';
import type { PatchLogLocale, PatchLogPublic } from './types';
import './patch-logs.css';

/**
 * Above the app frame and the first-login wizard (8500), below the image zoom
 * viewer (9500) and the notification tier (10001). Lumen dialogs sit at z-500
 * by default; the content takes this value inline and patch-logs.css lifts the
 * dialog's own scrim to the same tier (the overlay is the content's previous
 * sibling in <body>).
 */
export const PATCH_LOG_MODAL_Z_INDEX = 9000;

/** Slack before the container counts as scrolled to the bottom (sub-pixel rounding). */
const BOTTOM_TOLERANCE_PX = 8;

/** Lumen dialogs close on these by default; this one is a one-way door. */
const keepOpen = (event: Event) => event.preventDefault();

export interface PatchLogModalProps {
  opened: boolean;
  /**
   * Newest first. Several logs merge into one modal with a version line each,
   * and logs sharing a version collapse into a single line — a version the user
   * has already seen a heading for must not appear twice.
   */
  logs: PatchLogPublic[];
  locale: PatchLogLocale;
  /** Omit to hide the in-modal language switcher (e.g. a fixed-locale preview). */
  onLocaleChange?: (locale: PatchLogLocale) => void;
  /** Acknowledges every log shown. */
  onConfirm?: () => void;
  /** Omit to hide "Remind me later" (e.g. the history view or a backoffice preview). */
  onDefer?: () => void;
  confirming?: boolean;
  zIndex?: number;
}

/**
 * Presentational, blocking "What's New" modal on the Lumen Dialog: one raised
 * card with hairline rules between the masthead, the versions, the entries and
 * the total. Owns nothing but scroll state.
 *
 * Deliberately a one-way door: no X, no Esc, no click-outside. The only exits
 * are Confirm (at the end of the content) and, if offered, Remind Later —
 * position is the read gate, so nothing may dismiss it early. For the same
 * reason the entry cards get no zoom here: the modal must never open an overlay
 * above itself. Read-only surfaces (the history page) render the entry cards
 * directly instead of reusing this shell.
 */
export const PatchLogModal = ({
  opened,
  logs,
  locale,
  onLocaleChange,
  onConfirm,
  onDefer,
  confirming = false,
  zIndex = PATCH_LOG_MODAL_Z_INDEX,
}: PatchLogModalProps) => {
  const t = useCallback((key: Parameters<typeof resolvePatchLogCopy>[1]) => resolvePatchLogCopy(locale, key), [locale]);

  // Captured through a callback ref: the dialog content mounts in a portal a
  // render after `opened` flips, so a plain ref would still be null when the
  // effect first runs.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // One section per version, not per row: `logs` can carry the same version
  // more than once when a release was published in several passes.
  const visibleLogs = useMemo(() => collapsePatchLogsByVersion(logs), [logs]);

  const evaluateScroll = useCallback(() => {
    if (!scrollEl) {
      return;
    }
    const remaining = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
    setShowScrollHint(remaining > BOTTOM_TOLERANCE_PX);
  }, [scrollEl]);

  // Re-measure on scroll and whenever the container or its content resizes
  // (viewport change, language switch, an image that had no size hint).
  useEffect(() => {
    if (!scrollEl) {
      return;
    }
    evaluateScroll();
    scrollEl.addEventListener('scroll', evaluateScroll, { passive: true });
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(evaluateScroll);
      observer.observe(scrollEl);
      if (scrollEl.firstElementChild) {
        observer.observe(scrollEl.firstElementChild);
      }
    }
    return () => {
      scrollEl.removeEventListener('scroll', evaluateScroll);
      observer?.disconnect();
    };
  }, [scrollEl, evaluateScroll, visibleLogs.length]);

  // The "total" line: entries across every merged log, counted after
  // collapsing so it matches the cards on screen.
  const totalEntries = useMemo(() => visibleLogs.reduce((sum, log) => sum + log.entries.length, 0), [visibleLogs]);

  return (
    // Controlled by the host only: no Radix dismissal path reaches onOpenChange.
    <Dialog open={opened}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        onEscapeKeyDown={keepOpen}
        onPointerDownOutside={keepOpen}
        onInteractOutside={keepOpen}
        data-patch-log-modal=""
        style={{ zIndex }}
        className={cn(
          'jarvis-patch-log flex h-[min(718px,calc(100dvh-64px))] max-h-[min(718px,calc(100dvh-64px))] w-[calc(100%-2rem)] max-w-[472px] flex-col gap-0 overflow-hidden rounded-panel border-line-strong bg-raised p-0 sm:max-w-[472px]',
          locale === 'zh' && 'jarvis-patch-log--zh'
        )}
      >
        {/* The masthead stays outside the scroll area, so title and controls stay put. */}
        <div className="jarvis-patch-log__masthead">
          <DialogTitle className="jarvis-patch-log__title">{t('title')}</DialogTitle>

          <div className="jarvis-patch-log__header-actions">
            {onLocaleChange ? <PatchLogLocaleSwitcher locale={locale} onChange={onLocaleChange} /> : <span />}
            {onDefer ? (
              <Button type="button" variant="ghost" size="sm" onClick={onDefer} disabled={confirming}>
                {t('remindLater')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="jarvis-patch-log__viewport">
          {/* Focusable on purpose (axe: scrollable-region-focusable) — keyboard users must be able to scroll it. */}
          <div
            ref={setScrollEl}
            className="jarvis-patch-log__scroll"
            // biome-ignore lint/a11y/noNoninteractiveTabindex: scrollable region must be reachable by keyboard
            tabIndex={0}
            role="region"
            aria-label={t('title')}
          >
            <div className="jarvis-patch-log__content">
              {visibleLogs.map((log, logIndex) => (
                <section key={log.id} aria-label={log.versionLabel}>
                  <p className="jarvis-patch-log__version font-mono">
                    {formatPatchLogVersionLabel(log.versionLabel, t('version'))}
                    {' — '}
                    {formatPatchLogDate(log.releaseDate, locale)}
                  </p>
                  <div className="jarvis-patch-log__entries">
                    {log.entries.map((entry, entryIndex) => (
                      <PatchLogEntryCard
                        key={entry.id}
                        entry={entry}
                        locale={locale}
                        eager={logIndex === 0 && entryIndex === 0}
                      />
                    ))}
                  </div>
                </section>
              ))}

              {visibleLogs.length === 0 ? <p className="jarvis-patch-log__empty">{t('historyEmpty')}</p> : null}

              <p className="jarvis-patch-log__total font-mono">
                <span>{t('totalChanges')}</span>
                <span className="jarvis-patch-log__total-value">{totalEntries}</span>
              </p>

              {/* Confirm lives at the END of the content on purpose: reaching it means the user scrolled past everything. */}
              <div className="jarvis-patch-log__confirm-row">
                <Button
                  type="button"
                  className="h-10.5 w-full"
                  disabled={confirming}
                  aria-busy={confirming || undefined}
                  onClick={onConfirm}
                >
                  {confirming ? (
                    <Loader2Icon className="animate-spin" aria-hidden="true" />
                  ) : onConfirm ? (
                    <CheckIcon aria-hidden="true" />
                  ) : null}
                  {onConfirm ? t('confirm') : t('close')}
                </Button>
              </div>
            </div>
          </div>

          {showScrollHint ? (
            <div className="jarvis-patch-log__hint" aria-hidden="true">
              <ChevronDownIcon className="jarvis-patch-log__hint-chevron size-5.5" strokeWidth={2.2} />
              <span>{t('scrollHint')}</span>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PatchLogModal;
